import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import Chunk from 'nanogl-pbr/Chunk'
import ChunksSlots from 'nanogl-pbr/ChunksSlots'
import Input, { ShaderType, Uniform } from 'nanogl-pbr/Input'
import TexCoord from 'nanogl-pbr/TexCoord'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { HexToNewVec3 } from '@/webgl/core/Color'
import { RenderContext } from '@/webgl/core/RenderContext'
import gui from '@/webgl/dev/gui'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { backgroundCutout } from '@/webgl/runner/rendering/RenderingUtils'

export class SmokeChunk extends Chunk {
  private time: Input
  private uvs: Input
  private uvs2: Input
  private sine: Input
  private color: Input
  private reveal: Input

  public timeUniform: Uniform
  public colorUniform: Uniform
  public revealUniform: Uniform

  private waveSettings: Uniform

  get WaveLengh() {
    return this.waveSettings.value[0]
  }
  set WaveLengh(v) {
    this.waveSettings.value[0] = v
  }

  get WaveOffset() {
    return this.waveSettings.value[1]
  }
  set WaveOffset(v) {
    this.waveSettings.value[1] = v
  }

  get WaveAmplitude() {
    return this.waveSettings.value[2]
  }
  set WaveAmplitude(v) {
    this.waveSettings.value[2] = v
  }

  get WaveSpeed() {
    return this.waveSettings.value[3]
  }
  set WaveSpeed(v) {
    this.waveSettings.value[3] = v
  }

  get Reveal() {
    return this.revealUniform ? this.revealUniform.value[0] : 0
  }
  set Reveal(v) {
    this.revealUniform?.set(v)
  }

  constructor() {
    super(true, false)
    this.addChild((this.time = new Input('ScrollTime', 1, ShaderType.FRAGMENT)))
    this.addChild((this.uvs = new Input('TexCoord', 2, ShaderType.FRAGMENT)))
    this.addChild((this.sine = new Input('SineSettings', 4, ShaderType.FRAGMENT)))
    this.addChild((this.color = new Input('Color', 3, ShaderType.FRAGMENT)))

    this.addChild((this.uvs2 = new Input('TexCoord1', 2, ShaderType.FRAGMENT)))
    this.addChild((this.reveal = new Input('Reveal', 1, ShaderType.FRAGMENT)))

    this.uvs.attachAttribute(TexCoord.create('aTexCoord0').attrib)
    this.timeUniform = this.time.attachUniform('uScrollTime', 1)
    this.colorUniform = this.color.attachUniform('uColor', 3)
    this.colorUniform.set(1, 1, 1)

    this.waveSettings = this.sine.attachUniform('uSineSettings', 4)

    this.WaveLengh = 29.89
    this.WaveOffset = 0.0695652174
    this.WaveAmplitude = 0.0684782609
    this.WaveSpeed = 1.8
  }

  useReveal() {
    this.uvs2.attachAttribute(TexCoord.create('aTexCoord1').attrib)
    this.revealUniform = this.reveal.attachUniform('uReveal', 1)
    this.revealUniform.set(0)
  }

  setColor(value: number) {
    this.colorUniform.set(...HexToNewVec3(value))
  }

  _genCode(slots: ChunksSlots): void {
    slots.add(
      'pf',
      /*glsl*/ `
      #define WaveLengh SineSettings().x
      #define WaveOffset SineSettings().y
      #define WaveAmplitude SineSettings().z
      #define WaveSpeed SineSettings().w
    `
    )

    slots.add(
      'postf',
      /*glsl*/ `

      float x = TexCoord().x;
      float invx = 1.0 - x;
      float y = TexCoord().y;
      float scroll = WaveSpeed * ScrollTime();
      float secondary = 0.01;
      
      float curve = WaveAmplitude * sin((WaveLengh * x) + scroll) + WaveOffset + secondary;
      
      float offset = sin((WaveLengh * 2.0 * x) + (-scroll * 2.)) * invx * 2.0;
      curve += offset * secondary;
      
      float c = TexCoord().y - curve;
      float line = smoothstep(0.0, 0.005, c) - smoothstep(0.005, 0.002, TexCoord().y - curve);
      float alpha = smoothstep(0., 0.002, c);


      #if HAS_Reveal

      float rprogress = Reveal() / 0.4;
      rprogress = clamp(rprogress, 0.0, 1.0);
      float r = TexCoord1().x + sin((TexCoord1().y + 25.0) * 20.0 + ScrollTime() * 2.0) * 0.01;

      float sample1 = rprogress - r;
      
      alpha *= step(0.01, sample1);
      line *= step(0.011, sample1);

      float hprogress = (Reveal() - (1.0 - 0.4)) / 0.4;
      hprogress = clamp(hprogress, 0.0, 1.0);

      float sample2 = hprogress - r;
      alpha *= 1.0 - step(0.01, sample2);
      line *= 1.0 - step(0.011, sample2);

      #endif

      vec3 color = mix(vec3(0.0), Color(), line);

      FragColor.rgb = clamp(color, vec3(0.0), vec3(1.0));

      FragColor.a = alpha;

      if( FragColor.a < 0.001 ) discard;
      `
    )
  }
}

export default class SmokeRenderer implements ILoadable {
  node: Node = new Node()

  gltf: Gltf
  chunk: SmokeChunk
  active: boolean = true

  public constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.world.root.add(this.node)

    this.chunk = new SmokeChunk()
    const fld = gui.folder('Smoke')
    fld.add(this.chunk, 'WaveLengh', { min: 0, max: 50 })
    fld.add(this.chunk, 'WaveOffset', { min: 0, max: 0.2 })
    fld.add(this.chunk, 'WaveAmplitude', { min: 0, max: 0.3 })
    fld.add(this.chunk, 'WaveSpeed', { min: 0, max: 20 })
    fld.add(this.chunk, 'Reveal', { min: 0, max: 1 })
  }

  setActive(flag: boolean) {
    this.active = flag
  }

  async load(abort: AbortSignal): Promise<void> {
    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()

    overrides.overridePass('smoke', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.glconfig.enableBlend(true)
      pass.glconfig.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
      backgroundCutout(this.gl, pass)
      pass.inputs.add(this.chunk)
      return passInstance.pass
    })

    this.gltf = await new GltfResource('common/smoke.glb', this.gl, { extensions: [overrides] }).load()
    this.node.add(this.gltf.root)
  }

  public setColor(color: number) {
    this.chunk.setColor(color)
  }

  public preRender(time: TimeEntity) {
    if (!this.active) return
    const settings = this.world.settings
    this.chunk.timeUniform.set(this.chunk.timeUniform.value[0] + time.deltaTime * settings.scrollSpeed)
  }

  public render(context: RenderContext) {
    if (!this.active) return
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }
  }
}
